import axios from 'axios'
import Messages from '../utils/messages'
import LoadingManager from '../utils/loading'
import MessageBox from '../utils/message-box'
import { AJAX_DEFAULT_TIMEOUT } from '../defines/const'
import CommonUtil from '../utils/common'

export const Code = {
  SUCCESS: '0001',
  TIMEOUT: '1001',
  NO_CONTRACT: '2911',
  MODEL_HAS_ALREADY_DELETED: '3001',
  MODEL_USAGE_LIMIT_HAS_BEEN_REACHED: '3991',
  API_USAGE_LIMIT_HAS_BEEN_REACHED: '3992',
  STORAGE_USAGE_LIMIT_HAS_BEEN_REACHED: '3993',
  MODEL_DOES_NOT_EXISTS: '5008',
  INTERNAL_SERVER_ERROR: '9821'
}

export default class BaseApiClient {

  static get (context, url, params, options) {
    const http = this.getClient()
    const config = Object.assign(options || {}, {
      context,
      params
    })
    this.showLoading(context, options)
    return http.get(url, config)
      .then(this.onInterceptResponse)
      .catch(this.onInterceptRejectResponse)
  }

  static post (context, url, params, options) {
    const http = this.getClient()
    const config = Object.assign(options || {}, {
      context
    })
    this.showLoading(context, options)
    return http.post(url, params, config)
      .then(this.onInterceptResponse)
      .catch(this.onInterceptRejectResponse)
  }

  static put (context, url, params, options) {
    const http = this.getClient()
    const config = Object.assign(options || {}, {
      context
    })
    this.showLoading(context, options)
    return http.put(url, params, config)
      .then(this.onInterceptResponse)
      .catch(this.onInterceptRejectResponse)
  }

  static delete (context, url, params, options) {
    const http = this.getClient()
    const config = Object.assign(options || {}, {
      context,
      data: params
    })
    this.showLoading(context, options)
    return http.delete(url, config)
      .then(this.onInterceptResponse)
      .catch(this.onInterceptRejectResponse)
  }

  static showWarningMessage (resolve, response, context, message) {
    MessageBox.alert(context, message, {
      dangerouslyUseHTMLString: true,
      callback () {
        resolve(response)
      }
    }).catch(() => {})
  }

  static changeLocationModelList (context, code) {
    if (CommonUtil.isNotNull(context)) {
      context.$router.push({
        name: 'model-list'
      })
    }
  }

  static changeLocationError (context, code) {
    if (CommonUtil.isNotNull(context)) {
      context.$router.push({
        name: 'error',
        params: {
          code
        }
      })
    }
  }

  static changeLocationRestrictionsError (context, code) {
    if (CommonUtil.isNotNull(context)) {
      context.$router.push({
        name: 'restrictions-error',
        params: {
          code
        }
      })
    }
  }

  static changeLocationNoContractError (context) {
    if (CommonUtil.isNotNull(context)) {
      context.$router.push({
        name: 'no-contract-error'
      })
    }
  }

  static BASE_URL = ''

  static getClient () {
    const http = axios.create({
      baseURL: this.BASE_URL,
      timeout: AJAX_DEFAULT_TIMEOUT,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    http.interceptors.request.use(this.onInterceptRequest,
      this.onInterceptRejectRequest)

    http.defaults.withCredentials = true
    return http
  }

  static showLoading (context, options) {
    if (context &&
      (options == null || (options && options.loading !== false))) {
      options = Object.assign({}, options)
      LoadingManager.show(context, options.loadingMessage)
    }
  }

  static hideLoading (config) {
    const context = config.context
    const loading = config.loading

    if (CommonUtil.isNotEmpty(context) &&
      (loading == null || loading !== false)) {
      LoadingManager.hide(context)
    }
  }

  static onInterceptRequest (config) {
    if (config.url.slice(-1) !== '/') {
      config.url = `${config.url}/`
    }
    return config
  }

  static onInterceptRejectRequest (error) {
    BaseApiClient.hideLoading(error.config)
    return Promise.reject(error)
  }

  static onInterceptResponse (response) {
    BaseApiClient.hideLoading(response.config)
    return new Promise((resolve, reject) => {
      if (response.status === 200) {
        const data = response.data
        const config = response.config
        const context = config.context
        const code = data.code

        if ([
          Code.MODEL_USAGE_LIMIT_HAS_BEEN_REACHED,
          Code.API_USAGE_LIMIT_HAS_BEEN_REACHED,
          Code.STORAGE_USAGE_LIMIT_HAS_BEEN_REACHED].indexOf(
          response.data.code) >= 0) {
          if (context) {
            BaseApiClient.changeLocationRestrictionsError(context, code)
          } else {
            return resolve(response)
          }
        } else if (Code.NO_CONTRACT === code) {
          if (context) {
            BaseApiClient.changeLocationNoContractError(context)
          } else {
            return resolve(response)
          }
        } else if (/^[235]\d{3}$/.test(code) && !/^21\d{2}$/.test(code)) {
          if (context) {
            const message = Messages.getMessage(code,
              data.messageArgs || data.message_args, config.messageReplaceId,
              data.message)
            if ([
              Code.MODEL_HAS_ALREADY_DELETED,
              Code.MODEL_DOES_NOT_EXISTS].indexOf(code) < 0) {
              BaseApiClient.showWarningMessage(resolve, response, context,
                message)
            } else {
              BaseApiClient.showWarningMessage((resolve) => {
                BaseApiClient.changeLocationModelList(context, code)
              }, response, context, message)
            }
          } else {
            return resolve(response)
          }
        } else if (/^9\d{3}$/.test(code)) {
          if (context) {
            BaseApiClient.changeLocationError(context, code)
          }
          return reject({
            config: response.config,
            code: 200,
            request: response.request,
            response
          })
        } else {
          return resolve(response)
        }
      }

      return response
    })
  }

  static onInterceptRejectResponse (error) {
    return new Promise((resolve, reject) => {
      if (axios.isCancel(error)) {
        return reject(error)
      }

      BaseApiClient.hideLoading(error.config)

      switch (error.response.status) {
          case 403:
            window.location.href = '/'
            return
          case 500:
            const config = error.config
            const context = config.context
            BaseApiClient.changeLocationError(context,
              Code.INTERNAL_SERVER_ERROR)
            return reject(error)
          case 503:
            window.location.href = '/'
            return
          default:
            return reject(error)
      }
    })
  }
}



// WEBPACK FOOTER //
// ./src/api/base.js